import React, { useState, useMemo } from "react";
import { format } from "date-fns";
import { X, CheckCircle2, XCircle, Clock, ChevronDown, ChevronRight, Search, Check, Pencil } from "lucide-react";
import { DateTimeUtil } from "../../../../utils";
import { SchedulingToolUtils } from "../../../../utils/schedulingToolConfig";
import { NgwAvatar } from "../../../../components/common";
import type { ScheduleV3Schedule, ScheduleV3Group, ScheduleV3Timeoff, ScheduleV3Unavailability } from "../../../../types";

/** Vertical gradient for the left bar (darker at top, lighter at bottom) - matches calendar card style */
function getGradientStyle(type: "schedule" | "timeoff" | "unavailability" | "openShift", scheduleColor?: string): React.CSSProperties {
    if (type === "schedule" && scheduleColor) {
        return {
            background: `linear-gradient(180deg, ${scheduleColor} 0%, ${scheduleColor}e6 100%)`,
        };
    }
    const gradients: Record<string, string> = {
        timeoff: "linear-gradient(180deg, #c2410c 0%, #ea580c 50%, #fb923c 100%)",
        unavailability: "linear-gradient(180deg, #4b5563 0%, #6b7280 50%, #9ca3af 100%)",
        openShift: "linear-gradient(180deg, #6d28d9 0%, #7c3aed 50%, #a78bfa 100%)",
    };
    return { background: gradients[type] || gradients.unavailability };
}

function getInitials(name: string | undefined | null): string {
    if (!name || typeof name !== "string") return "?";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
}

function hexToRgba(hex: string, alpha: number): string | null {
    if (typeof hex !== "string") return null;
    const raw = hex.trim().replace("#", "");
    if (!raw) return null;
    const normalized =
        raw.length === 3
            ? raw
                  .split("")
                  .map((c) => c + c)
                  .join("")
            : raw.length === 6
              ? raw
              : null;
    if (!normalized) return null;
    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export interface ShowMoreItemSchedule {
    schedule: ScheduleV3Schedule;
    groupName: string;
    group: ScheduleV3Group;
}

export interface ShowMoreItemTimeoff {
    timeoff: ScheduleV3Timeoff;
    groupName: string;
    group: ScheduleV3Group;
}

export interface ShowMoreItemUnavailability {
    unavailability: ScheduleV3Unavailability;
    groupName: string;
    group: ScheduleV3Group;
}

export interface ShowMoreItemOpenShift {
    openShift: {
        shift_details?: { shift_name?: string; shift_code?: string; shift_start_time?: string; shift_end_time?: string };
        manager_details?: { name?: string };
        open_shift_id?: string;
        os_sch_open_shift_transactions_id?: string;
        shift_date?: string;
        status?: string;
    };
    groupName: string;
    group: ScheduleV3Group;
}

export interface ShowMoreModalProps {
    open: boolean;
    date: Date | null;
    items: {
        schedules: ShowMoreItemSchedule[];
        timeoffs: ShowMoreItemTimeoff[];
        openShifts: ShowMoreItemOpenShift[];
        unavailability?: ShowMoreItemUnavailability[];
    };
    groupingType: string;
    onClose: () => void;
    onScheduleClick: (schedule: ScheduleV3Schedule, date: Date, group?: ScheduleV3Group) => void;
    /** When a timeoff item with TIME_OFF_REQUEST_ID or timeoff_id (when schedules_id empty) is clicked, open timeoff detail modal. */
    onTimeoffClick?: (item: ShowMoreItemTimeoff) => void;
    /** When an unavailability item is clicked, open unavailability detail modal. */
    onUnavailabilityClick?: (item: ShowMoreItemUnavailability) => void;
    /** When an open shift item is clicked, open open shift detail modal. */
    onOpenShiftClick?: (item: ShowMoreItemOpenShift) => void;
}

function formatTimeForParser(timeStr: string): string {
    if (!timeStr) return timeStr;
    if (timeStr.includes("T") || timeStr.includes(" ")) return timeStr;
    const timeWithSeconds =
        timeStr.length === 5 && timeStr.includes(":") ? `${timeStr}:00` : timeStr;
    return `2000-01-01 ${timeWithSeconds}`;
}

/** Resolve time-off request ID for opening detail modal. Prefer TIME_OFF_REQUEST_ID; if missing and schedules_id is empty, use timeoff_id. */
function getEffectiveTimeoffRequestId(timeoff: ScheduleV3Timeoff): string | null {
    const requestId = timeoff.other_details?.TIME_OFF_REQUEST_ID;
    const hasRequestId = requestId != null && String(requestId).trim() !== "";
    if (hasRequestId) return String(requestId).trim();
    const schedulesId = timeoff.schedules_id ?? null;
    const schedulesIdEmpty = schedulesId == null || String(schedulesId).trim() === "";
    if (schedulesIdEmpty && timeoff.timeoff_id != null && String(timeoff.timeoff_id).trim() !== "") {
        return String(timeoff.timeoff_id).trim();
    }
    return null;
}

function formatUnavailabilityTimeRange(start?: string | null, end?: string | null): string {
    if (!start || !end || typeof start !== "string" || typeof end !== "string") return "All day";
    const trimmedStart = start.trim();
    const trimmedEnd = end.trim();
    if (!trimmedStart || !trimmedEnd) return "All day";
    const result = DateTimeUtil.prettyPrintTimeRange(trimmedStart, trimmedEnd);
    return result === "N/A" || result === "Invalid time" ? "All day" : result;
}

function matchesSearch(text: string | undefined | null, query: string): boolean {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    const t = (text ?? "").toString().toLowerCase();
    return t.includes(q);
}

function filterSchedulesBySearch(
    schedules: ShowMoreItemSchedule[],
    query: string
): ShowMoreItemSchedule[] {
    if (!query.trim()) return schedules;
    return schedules.filter((item) => {
        const name = item.schedule.employee?.name ?? "";
        const shiftName = item.schedule.shift?.shift_name ?? "";
        return matchesSearch(name, query) || matchesSearch(shiftName, query);
    });
}

function filterTimeoffsBySearch(
    timeoffs: ShowMoreItemTimeoff[],
    query: string
): ShowMoreItemTimeoff[] {
    if (!query.trim()) return timeoffs;
    return timeoffs.filter((item) => {
        const name = item.timeoff.employee?.name ?? "";
        const code = item.timeoff.earnings_code_name ?? "";
        const notes = item.timeoff.notes ?? "";
        return matchesSearch(name, query) || matchesSearch(code, query) || matchesSearch(notes, query);
    });
}

function filterUnavailabilityBySearch(
    items: ShowMoreItemUnavailability[],
    query: string
): ShowMoreItemUnavailability[] {
    if (!query.trim()) return items;
    return items.filter((item) => {
        const name =
            (typeof item.unavailability.employee?.name === "string" && item.unavailability.employee.name) ||
            (typeof (item.unavailability as any).employee_name === "string" && (item.unavailability as any).employee_name) ||
            "";
        return matchesSearch(name, query);
    });
}

function filterOpenShiftsBySearch(
    openShifts: ShowMoreItemOpenShift[],
    query: string
): ShowMoreItemOpenShift[] {
    if (!query.trim()) return openShifts;
    return openShifts.filter((item) => {
        const shiftName = item.openShift.shift_details?.shift_name ?? "";
        const shiftCode = item.openShift.shift_details?.shift_code ?? "";
        const managerName = item.openShift.manager_details?.name ?? "";
        return matchesSearch(shiftName, query) || matchesSearch(shiftCode, query) || matchesSearch(managerName, query);
    });
}

const ShowMoreModal: React.FC<ShowMoreModalProps> = ({
    open,
    date,
    items,
    groupingType,
    onClose,
    onScheduleClick,
    onTimeoffClick,
    onUnavailabilityClick,
    onOpenShiftClick,
}) => {
    const [schedulesOpen, setSchedulesOpen] = useState(true);
    const [timeoffsOpen, setTimeoffsOpen] = useState(true);
    const [unavailabilityOpen, setUnavailabilityOpen] = useState(true);
    const [openShiftsOpen, setOpenShiftsOpen] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const showSchedules = true;
    const showTimeoffs = true;
    const showUnavailability = true;
    const showOpenShifts = true;

    const unavailabilityItems = items?.unavailability ?? [];

    const filteredSchedules = useMemo(() => {
        const list = showSchedules ? filterSchedulesBySearch(items.schedules ?? [], searchQuery) : [];
        return list;
    }, [items.schedules, searchQuery, showSchedules]);

    const filteredTimeoffs = useMemo(() => {
        const list = showTimeoffs ? filterTimeoffsBySearch(items.timeoffs ?? [], searchQuery) : [];
        return list;
    }, [items.timeoffs, searchQuery, showTimeoffs]);

    const filteredUnavailability = useMemo(() => {
        const list = showUnavailability ? filterUnavailabilityBySearch(unavailabilityItems, searchQuery) : [];
        return list;
    }, [unavailabilityItems, searchQuery, showUnavailability]);

    const filteredOpenShifts = useMemo(() => {
        const list = showOpenShifts ? filterOpenShiftsBySearch(items.openShifts ?? [], searchQuery) : [];
        return list;
    }, [items.openShifts, searchQuery, showOpenShifts]);

    const totalFilteredItems =
        filteredSchedules.length +
        filteredTimeoffs.length +
        filteredUnavailability.length +
        filteredOpenShifts.length;

    if (!open || !date) return null;

    const dateStr = format(date, "EEEE, MMMM d, yyyy");

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1400]">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] m-4 flex flex-col">
                <div className="p-6 border-b border-gray-200 space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-xl font-bold text-gray-900">All Items</h3>
                            <p className="text-sm text-gray-600 mt-1">{dateStr}</p>
                            <p className="text-xs text-gray-500 mt-1">
                                {totalFilteredItems} total items
                                {(searchQuery.trim() || !showSchedules || !showTimeoffs || !showUnavailability || !showOpenShifts) && (
                                    <span className="text-gray-400"> (filtered)</span>
                                )}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X size={24} />
                        </button>
                    </div>
                    <div className="flex flex-col gap-3">
                        <div className="relative">
                            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by name, shift, code..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            />
                            {searchQuery.trim() && (
                                <button
                                    type="button"
                                    onClick={() => setSearchQuery("")}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                                    aria-label="Clear search"
                                    title="Clear"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                        {/* Type toggles removed (search-only filter) */}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {showSchedules && (
                        <div>
                            <button
                                type="button"
                                onClick={() => setSchedulesOpen((prev) => !prev)}
                                className="w-full text-left flex items-center justify-between gap-2 mb-3 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1 rounded"
                            >
                                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full bg-teal-600"></span>
                                    Schedules ({filteredSchedules.length})
                                </h4>
                                {schedulesOpen ? (
                                    <ChevronDown size={18} className="text-gray-500 flex-shrink-0" />
                                ) : (
                                    <ChevronRight size={18} className="text-gray-500 flex-shrink-0" />
                                )}
                            </button>
                            {schedulesOpen && (
                            <div className="space-y-2">
                                {filteredSchedules.map((item, idx) => {
                                    const { schedule, groupName, group } = item;
                                    const scheduleColor = schedule.shift?.shift_color || "#4CAF50";
                                    const employeeName = schedule.employee?.name || "Unknown";
                                    const employeeImage = (schedule.employee as any)?.image_url || "";
                                    const startTime = schedule.shift?.start_time
                                        ? formatTimeForParser(schedule.shift.start_time)
                                        : null;
                                    const endTime = schedule.shift?.end_time
                                        ? formatTimeForParser(schedule.shift.end_time)
                                        : null;
                                    const timeRange =
                                        startTime && endTime
                                            ? DateTimeUtil.formatElegantTimeRange(startTime, endTime)
                                            : "";
                                    const displayTimeRange =
                                        timeRange && timeRange !== "N/A" ? timeRange : "";

                                    return (
                                        <div
                                            key={idx}
                                            className="relative w-full px-3 py-2 rounded-lg border border-gray-200 shadow-sm hover:shadow transition-colors overflow-hidden cursor-pointer bg-white hover:bg-gray-50"
                                            onClick={() => {
                                                onScheduleClick(schedule, date, group);
                                            }}
                                            style={{
                                                backgroundColor:
                                                    hexToRgba(scheduleColor, 0.06) ?? undefined,
                                            }}
                                        >
                                            <div
                                                className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-lg"
                                                style={getGradientStyle("schedule", scheduleColor)}
                                            />
                                            <div className="flex flex-col gap-0.5 w-full pl-4">
                                                <div className="flex items-center justify-between gap-2 min-w-0">
                                                    <div className="font-medium text-gray-900 truncate flex items-center gap-2">
                                                        <NgwAvatar
                                                            src={employeeImage || undefined}
                                                            name={employeeName}
                                                            className="w-7 h-7 text-xs"
                                                        />
                                                        <span className="truncate">
                                                            <span className="text-gray-900 font-medium">{employeeName}</span>
                                                            <span className="text-gray-700 font-normal">
                                                                {" • "}
                                                                {schedule.shift?.shift_name || "Shift"}
                                                                {displayTimeRange ? ` (${displayTimeRange})` : ""}
                                                            </span>
                                                        </span>
                                                    </div>
                                                    {(() => {
                                                        const publishInfo =
                                                            SchedulingToolUtils.getPublishStatusInfo(schedule);
                                                        if (!publishInfo) return null;
                                                        const IconComponent =
                                                            publishInfo.iconName === "XCircle"
                                                                ? XCircle
                                                                : publishInfo.iconName === "CheckCircle2"
                                                                  ? CheckCircle2
                                                                  : Clock;
                                                        return (
                                                            <span
                                                                className={`inline-flex items-center ${publishInfo.isUnpublished ? 'bg-orange-500 rounded-full p-0.5 text-white' : 'text-green-500'} flex-shrink-0`}
                                                                title={publishInfo.tooltip}
                                                            >
                                                                <IconComponent size={14} />
                                                            </span>
                                                        );
                                                    })()}
                                                </div>
                                                {groupingType !== "employee" && (
                                                    <div className="text-xs text-gray-500 truncate mt-1">{groupName}</div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            )}
                        </div>
                    )}

                    {showTimeoffs && (
                        <div>
                            <button
                                type="button"
                                onClick={() => setTimeoffsOpen((prev) => !prev)}
                                className="w-full text-left flex items-center justify-between gap-2 mb-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 rounded"
                            >
                                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                                    Time Off ({filteredTimeoffs.length})
                                </h4>
                                {timeoffsOpen ? (
                                    <ChevronDown size={18} className="text-gray-500 flex-shrink-0" />
                                ) : (
                                    <ChevronRight size={18} className="text-gray-500 flex-shrink-0" />
                                )}
                            </button>
                            {timeoffsOpen && (
                            <div className="space-y-2">
                                {filteredTimeoffs.map((item, idx) => {
                                    const { timeoff, groupName } = item;
                                    const effectiveRequestId = getEffectiveTimeoffRequestId(timeoff);
                                    const hasRequestId = effectiveRequestId != null;
                                    const extractedStartRaw = DateTimeUtil.getTime24HourFormat(
                                        timeoff.start_datetime
                                    );
                                    const extractedEndRaw = DateTimeUtil.getTime24HourFormat(
                                        timeoff.end_datetime
                                    );
                                    const extractedStartTime =
                                        extractedStartRaw === "Invalid time" ? "" : extractedStartRaw;
                                    const extractedEndTime =
                                        extractedEndRaw === "Invalid time" ? "" : extractedEndRaw;
                                    const startTime =
                                        timeoff.start_datetime &&
                                        (timeoff.start_datetime.includes("T") ||
                                            timeoff.start_datetime.includes(" "))
                                            ? timeoff.start_datetime
                                            : extractedStartTime
                                              ? formatTimeForParser(extractedStartTime)
                                              : null;
                                    const endTime =
                                        timeoff.end_datetime &&
                                        (timeoff.end_datetime.includes("T") ||
                                            timeoff.end_datetime.includes(" "))
                                            ? timeoff.end_datetime
                                            : extractedEndTime
                                              ? formatTimeForParser(extractedEndTime)
                                              : null;
                                    const timeRange =
                                        startTime && endTime
                                            ? DateTimeUtil.formatElegantTimeRange(startTime, endTime)
                                            : "";
                                    const displayTimeRange =
                                        timeRange && timeRange !== "N/A" ? timeRange : "";
                                    const shouldShowTime = timeoff.show_time === true;
                                    const trackInDays = (timeoff as any).track_time_in === "Days";
                                    const labelNoTimePopover = (() => {
                                        if (
                                            trackInDays &&
                                            (timeoff.hours == 1 || Number(timeoff.hours) === 1)
                                        )
                                            return "Full day";
                                        if (
                                            trackInDays &&
                                            (timeoff.hours == 0.5 || Number(timeoff.hours) === 0.5)
                                        )
                                            return "Half day";
                                        return `${timeoff.hours}h`;
                                    })();
                                    const statusValue = timeoff.status;
                                    const isPending =
                                        statusValue &&
                                        String(statusValue).toUpperCase().trim() === "PENDING";
                                    const employeeName = timeoff.employee?.name || "Unknown";
                                    const employeeImage = (timeoff.employee as any)?.image_url || "";
                                    const timeoffSummary =
                                        (shouldShowTime && displayTimeRange) || timeoff.show_time === false
                                            ? `${timeoff.earnings_code_name} (${shouldShowTime ? displayTimeRange : labelNoTimePopover})`
                                            : `${timeoff.earnings_code_name} (${timeoff.hours}h)`;
                                    const secondaryParts: string[] = [];
                                    if (groupingType !== "employee" && groupName) {
                                        secondaryParts.push(groupName);
                                    }
                                    if (timeoff.notes) {
                                        secondaryParts.push(timeoff.notes);
                                    }

                                    return (
                                        <div
                                            key={idx}
                                            role={hasRequestId ? "button" : undefined}
                                            className={`relative w-full px-3 py-2 rounded-lg border shadow-sm transition-colors overflow-hidden border-orange-200 ${hasRequestId ? "cursor-pointer hover:shadow hover:bg-orange-50" : "hover:border-orange-300"}`}
                                            onClick={hasRequestId ? () => {
                                                onTimeoffClick?.(item);
                                            } : undefined}
                                            style={{ backgroundColor: "rgba(234, 88, 12, 0.06)" }}
                                        >
                                            <div
                                                className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-lg"
                                                style={getGradientStyle("timeoff")}
                                            />
                                            <div className="flex flex-col gap-0.5 w-full pl-4">
                                                <div className="flex items-center justify-between gap-2 min-w-0">
                                                    <div className="font-medium text-gray-900 truncate flex items-center gap-2">
                                                        <NgwAvatar
                                                            src={employeeImage || undefined}
                                                            name={employeeName}
                                                            className="w-7 h-7 text-xs"
                                                        />
                                                        <span className="truncate">
                                                            <span className="text-gray-900 font-medium">{employeeName}</span>
                                                            <span className="text-gray-700 font-normal"> • {timeoffSummary}</span>
                                                        </span>
                                                    </div>
                                                    {isPending && (
                                                        <div className="inline-flex items-center gap-1 text-sm text-amber-700 flex-shrink-0">
                                                            <Clock size={14} className="text-amber-600" />
                                                            <span>Pending</span>
                                                        </div>
                                                    )}
                                                </div>
                                                {secondaryParts.length > 0 && (
                                                    <div className="text-xs text-gray-500 truncate mt-1">
                                                        {secondaryParts.join(" • ")}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            )}
                        </div>
                    )}

                    {showUnavailability && (
                        <div>
                            <button
                                type="button"
                                onClick={() => setUnavailabilityOpen((prev) => !prev)}
                                className="w-full text-left flex items-center justify-between gap-2 mb-3 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1 rounded"
                            >
                                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full bg-gray-400"></span>
                                    Unavailability ({filteredUnavailability.length})
                                </h4>
                                {unavailabilityOpen ? (
                                    <ChevronDown size={18} className="text-gray-500 flex-shrink-0" />
                                ) : (
                                    <ChevronRight size={18} className="text-gray-500 flex-shrink-0" />
                                )}
                            </button>
                            {unavailabilityOpen && (
                            <div className="space-y-2">
                                {filteredUnavailability.map((item, idx) => {
                                    const { unavailability, groupName } = item;
                                    const rawStart =
                                        (unavailability as any)?.start_time ?? (unavailability as any)?.from_time ?? null;
                                    const rawEnd =
                                        (unavailability as any)?.end_time ?? (unavailability as any)?.to_time ?? null;
                                    const allDayValue = (unavailability as any).all_day;
                                    const isFullDay =
                                        unavailability.all_day === true ||
                                        allDayValue === "1" ||
                                        allDayValue === "true" ||
                                        allDayValue === 1 ||
                                        (!rawStart && !rawEnd);
                                    const prettyTimeRange = formatUnavailabilityTimeRange(rawStart, rawEnd);
                                    const unavailType = isFullDay ? "Full Day" : "Partial";
                                    const employeeName =
                                        (typeof unavailability.employee?.name === "string" && unavailability.employee.name) ||
                                        (typeof (unavailability as any).employee_name === "string" &&
                                            (unavailability as any).employee_name) ||
                                        "";
                                    const employeeImage =
                                        (unavailability as any)?.employee?.image_url ||
                                        (unavailability as any)?.employee_image_url ||
                                        (unavailability as any)?.employee_image ||
                                        "";
                                    const unavailStatus = (unavailability as any).status;
                                    const normalizedStatus = unavailStatus ? String(unavailStatus).toUpperCase().trim() : "";
                                    const isPending = ["PENDING"].includes(normalizedStatus);
                                    const isApproved = ["APPROVED", "APPROVE", "ACTIVE"].includes(normalizedStatus);
                                    const isRejected = ["REJECTED", "REJECT", "DENIED", "DECLINED"].includes(normalizedStatus);
                                    const displayText = isFullDay ? unavailType : prettyTimeRange;
                                    const statusLabel = isRejected
                                        ? "Rejected"
                                        : isApproved
                                          ? "Approved"
                                          : isPending
                                            ? "Pending"
                                            : "";

                                    return (
                                        <div
                                            key={idx}
                                            role="button"
                                            className="relative w-full px-3 py-2 rounded-lg border border-gray-200 shadow-sm hover:shadow hover:bg-gray-50 cursor-pointer transition-colors overflow-hidden"
                                            onClick={() => {
                                                onUnavailabilityClick?.(item);
                                            }}
                                            style={{ backgroundColor: "rgba(107, 114, 128, 0.06)" }}
                                        >
                                            <div
                                                className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-lg"
                                                style={getGradientStyle("unavailability")}
                                            />
                                            <div className="flex flex-col gap-0.5 w-full pl-4">
                                                <div className="flex items-center justify-between gap-2 min-w-0">
                                                    <div className="font-medium text-gray-900 truncate flex items-center gap-2">
                                                        <NgwAvatar
                                                            src={employeeImage || undefined}
                                                            name={employeeName || "Unavailability"}
                                                            className="w-7 h-7 text-xs"
                                                        />
                                                        <span className="truncate">
                                                            <span className="text-gray-900 font-medium">{employeeName || "Unavailability"}</span>
                                                            <span className="text-gray-700 font-normal"> • {displayText}</span>
                                                        </span>
                                                    </div>
                                                    {statusLabel && (
                                                        <div className="inline-flex items-center gap-1 text-sm text-gray-700 flex-shrink-0" title={normalizedStatus || undefined}>
                                                            {isRejected ? (
                                                                <XCircle size={14} className="text-red-500" />
                                                            ) : isApproved ? (
                                                                <CheckCircle2 size={14} className="text-green-600" />
                                                            ) : isPending ? (
                                                                <Clock size={14} className="text-amber-600" />
                                                            ) : null}
                                                            <span>{statusLabel}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                {/* Avoid redundant label like "Unavailability" under "Full Day • Unavailable" */}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            )}
                        </div>
                    )}

                    {showOpenShifts && (
                        <div>
                            <button
                                type="button"
                                onClick={() => setOpenShiftsOpen((prev) => !prev)}
                                className="w-full text-left flex items-center justify-between gap-2 mb-3 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-1 rounded"
                            >
                                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full bg-purple-400 border-2 border-dashed border-purple-600"></span>
                                    Open Shifts ({filteredOpenShifts.length})
                                </h4>
                                {openShiftsOpen ? (
                                    <ChevronDown size={18} className="text-gray-500 flex-shrink-0" />
                                ) : (
                                    <ChevronRight size={18} className="text-gray-500 flex-shrink-0" />
                                )}
                            </button>
                            {openShiftsOpen && (
                            <div className="space-y-2">
                                {filteredOpenShifts.map((item, idx) => {
                                    const { openShift, groupName } = item;
                                    const { shift_details, manager_details } = openShift;
                                    const extractedStartTime =
                                        shift_details?.shift_start_time?.substring(0, 5) || "";
                                    const extractedEndTime =
                                        shift_details?.shift_end_time?.substring(0, 5) || "";
                                    const startTime = extractedStartTime
                                        ? formatTimeForParser(extractedStartTime)
                                        : null;
                                    const endTime = extractedEndTime
                                        ? formatTimeForParser(extractedEndTime)
                                        : null;
                                    const timeRange =
                                        startTime && endTime
                                            ? DateTimeUtil.formatElegantTimeRange(startTime, endTime)
                                            : "";
                                    const displayTimeRange =
                                        timeRange && timeRange !== "N/A" ? timeRange : "";
                                    const openShiftStatusInfo = SchedulingToolUtils.getOpenShiftStatusInfo(openShift as any);
                                    const managerName = openShift.manager_details?.name || "Open Shift";
                                    const managerImage = (openShift as any)?.manager_details?.image_url || "";
                                    const shiftTitle = `${shift_details?.shift_name || "Open Shift"}`;
                                    const secondaryParts: string[] = [];
                                    if (groupingType !== "employee" && shift_details?.shift_name) {
                                        secondaryParts.push(`${shift_details?.shift_name}`);
                                    }
                                    if (openShift.manager_details?.name) {
                                        secondaryParts.push(`Created by: ${openShift.manager_details.name}`);
                                    }

                                    return (
                                        <div
                                            key={idx}
                                            role={onOpenShiftClick ? "button" : undefined}
                                            tabIndex={onOpenShiftClick ? 0 : undefined}
                                            onClick={onOpenShiftClick ? () => onOpenShiftClick(item) : undefined}
                                            onKeyDown={
                                                onOpenShiftClick
                                                    ? (e) => {
                                                          if (e.key === "Enter" || e.key === " ") {
                                                              e.preventDefault();
                                                              onOpenShiftClick(item);
                                                          }
                                                      }
                                                    : undefined
                                            }
                                            className={`relative w-full px-3 py-2 rounded-lg border-2 border-dashed border-purple-300 bg-purple-50 shadow-sm hover:shadow hover:border-purple-400 transition-colors overflow-hidden ${onOpenShiftClick ? "cursor-pointer" : ""}`}
                                            style={{ backgroundColor: "rgba(124, 58, 237, 0.06)" }}
                                        >
                                            <div
                                                className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-lg"
                                                style={getGradientStyle("openShift")}
                                            />
                                            <div className="flex flex-col gap-0.5 w-full pl-4">
                                                <div className="flex items-center justify-between gap-2 min-w-0">
                                                    <div className="font-medium text-purple-900 truncate flex items-center gap-2">
                                                        <NgwAvatar
                                                            src={managerImage || undefined}
                                                            name={managerName}
                                                            className="w-7 h-7 text-xs"
                                                        />
                                                        <span className="truncate">
                                                            <span className="text-purple-900 font-medium">{displayTimeRange || "Open Shift"}</span>
                                                            <span className="text-purple-700 font-normal"> • {shiftTitle}</span>
                                                        </span>
                                                    </div>
                                                    {openShiftStatusInfo && (
                                                        <span
                                                            className={`inline-flex items-center ${openShiftStatusInfo.isDraft ? "bg-amber-100 rounded-full p-0.5" : ""} ${openShiftStatusInfo.iconColor}`}
                                                            title={openShiftStatusInfo.tooltip}
                                                        >
                                                            {openShiftStatusInfo.iconName === "Pencil" ? (
                                                                <Pencil size={14} />
                                                            ) : (
                                                                <CheckCircle2 size={14} />
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                                {secondaryParts.length > 0 && (
                                                    <div className="text-xs text-gray-500 truncate mt-1">
                                                        {secondaryParts.join(" • ")}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-3 p-6 bg-gray-50 rounded-b-xl border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ShowMoreModal;
